'use client'

import { useEffect, useRef, useState } from 'react'
import { Loader2, Mic, Pause, Play } from 'lucide-react'
import { toast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { useCallRecordingAudio } from '@/features/cases/hooks/use-call-recording-audio'
import { AppError } from '@/lib/errors/appError'

/**
 * Playback control for a real call recording. `src` is fetched lazily (on first play) via the
 * backend's authenticated proxy — never a raw, pre-fetched Plivo URL — and cached as an object
 * URL for the life of this component, revoked on unmount.
 */
export function CallRecording({ callId, hasRecording }: { callId: string; hasRecording: boolean }) {
  const { fetchAudioUrl } = useCallRecordingAudio()
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [src, setSrc] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [playing, setPlaying] = useState(false)

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    const onEnded = () => setPlaying(false)
    audio.addEventListener('ended', onEnded)
    return () => audio.removeEventListener('ended', onEnded)
  }, [])

  useEffect(() => {
    return () => {
      if (src) URL.revokeObjectURL(src)
    }
  }, [src])

  if (!hasRecording) {
    return (
      <span className="text-muted-foreground inline-flex items-center gap-1.5 text-xs">
        <Mic className="size-3.5" /> No recording
      </span>
    )
  }

  const toggle = async () => {
    const audio = audioRef.current
    if (!audio) return

    if (playing) {
      audio.pause()
      setPlaying(false)
      return
    }

    if (!src) {
      setLoading(true)
      try {
        const url = await fetchAudioUrl(callId)
        setSrc(url)
        // `src` only takes effect on the next render — play once the element actually has it.
        requestAnimationFrame(() => {
          void audioRef.current?.play()
          setPlaying(true)
        })
      } catch (error) {
        toast({ variant: 'destructive', title: AppError.fromUnknown(error).message })
      } finally {
        setLoading(false)
      }
      return
    }

    void audio.play()
    setPlaying(true)
  }

  return (
    <div className="inline-flex items-center gap-1.5">
      <Button
        type="button"
        size="icon"
        variant="outline"
        className="size-7"
        onClick={() => void toggle()}
        disabled={loading}
        aria-label={playing ? 'Pause recording' : 'Play recording'}
      >
        {loading ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : playing ? (
          <Pause className="size-3.5" />
        ) : (
          <Play className="size-3.5" />
        )}
      </Button>
      <span className="text-muted-foreground text-xs">Recording</span>
      {/* Always rendered (even with no src yet) — `toggle()` needs `audioRef.current` to exist
       * on the very first click, before `fetchAudioUrl` has anything to set as `src`. */}
      <audio ref={audioRef} src={src ?? undefined} preload="none" />
    </div>
  )
}
